import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import api, { API } from "@/lib/api";
import { Routes, Route } from "react-router-dom";
import { toast } from "sonner";
import {
  AreaChart, Area, ResponsiveContainer, XAxis, YAxis, CartesianGrid, Tooltip,
  BarChart, Bar, PieChart, Pie, Cell, Legend
} from "recharts";
import {
  Calendar, Users, DollarSign, Image as ImageIcon, TrendingUp,
  Upload, Trash2, Edit2, Plus, Play, ChevronDown, ChevronUp, FolderPlus
} from "lucide-react";

function StatusSelect({ value, onChange, options, testid }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)}
      className="border border-input bg-background px-2 py-1 text-xs" data-testid={testid}>
      {options.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}

function Overview() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/analytics/overview").then(({ data }) => setData(data)).catch(() => {}); }, []);
  if (!data) return <div className="overline text-muted-foreground">Loading analytics…</div>;

  const stat = [
    { label: "Total Revenue", value: `PKR ${data.revenue?.toLocaleString() || 0}`, icon: DollarSign, sub: `Pending: ${data.pending_revenue?.toLocaleString() || 0}` },
    { label: "Bookings", value: data.total_bookings, icon: Calendar, sub: `${data.confirmed_bookings} confirmed` },
    { label: "Clients", value: data.total_clients, icon: Users, sub: `Active portal users` },
    { label: "Active Projects", value: data.active_projects, icon: ImageIcon, sub: `${data.completed_bookings} completed` },
  ];

  const COLORS = ["hsl(120 16% 25%)", "hsl(74 20% 76%)", "hsl(120 9% 50%)", "hsl(75 13% 90%)", "hsl(30 35% 55%)"];

  return (
    <div>
      <div className="mb-10">
        <div className="overline text-primary/70">Analytics</div>
        <h1 className="font-serif text-4xl md:text-5xl mt-2">Studio Overview</h1>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-10">
        {stat.map(s => (
          <div key={s.label} className="border border-border p-6 bg-card">
            <div className="flex justify-between items-start">
              <div className="overline text-muted-foreground">{s.label}</div>
              <s.icon size={18} strokeWidth={1.25} className="text-primary" />
            </div>
            <div className="font-serif text-3xl mt-3">{s.value}</div>
            <div className="text-xs text-muted-foreground mt-2">{s.sub}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 border border-border bg-card p-6">
          <div className="flex justify-between items-center mb-6">
            <h3 className="font-serif text-2xl">Revenue · last 6 months</h3>
            <TrendingUp size={18} strokeWidth={1.25} className="text-primary" />
          </div>
          <div className="h-72">
            <ResponsiveContainer>
              <AreaChart data={Array.isArray(data.monthly) ? data.monthly : []}>
                <defs>
                  <linearGradient id="olv" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="hsl(120 16% 25%)" stopOpacity={0.4}/>
                    <stop offset="100%" stopColor="hsl(120 16% 25%)" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="2 4" stroke="hsl(75 14% 83%)" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="hsl(120 7% 39%)" />
                <YAxis tick={{ fontSize: 11 }} stroke="hsl(120 7% 39%)" />
                <Tooltip contentStyle={{ borderRadius: 0, border: "1px solid hsl(75 14% 83%)" }} />
                <Area dataKey="revenue" stroke="hsl(120 16% 25%)" strokeWidth={2} fill="url(#olv)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="border border-border bg-card p-6">
          <h3 className="font-serif text-2xl mb-4">Bookings by Month</h3>
          <div className="h-72">
            <ResponsiveContainer>
              <BarChart data={Array.isArray(data.monthly) ? data.monthly : []}>
                <CartesianGrid strokeDasharray="2 4" stroke="hsl(75 14% 83%)" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip contentStyle={{ borderRadius: 0, border: "1px solid hsl(75 14% 83%)" }} />
                <Bar dataKey="bookings" fill="hsl(120 16% 25%)" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="lg:col-span-3 border border-border bg-card p-6">
          <h3 className="font-serif text-2xl mb-4">Portfolio by Category</h3>
          <div className="h-64">
            <ResponsiveContainer>
              <PieChart>
                <Pie data={Array.isArray(data.categories) ? data.categories : []} dataKey="count" nameKey="name" cx="50%" cy="50%" outerRadius={85}>
                  {(Array.isArray(data.categories) ? data.categories : []).map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ borderRadius: 0, border: "1px solid hsl(75 14% 83%)" }} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
}

function BookingsPage() {
  const [rows, setRows] = useState([]);
  const [expandedId, setExpandedId] = useState(null);

  const load = () => api.get("/bookings").then(({ data }) => setRows(Array.isArray(data) ? data : [])).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => {
    try {
      await api.put(`/bookings/${id}/status`, { status });
      toast.success("Status updated & event synced");
      load();
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Failed to update status");
    }
  };

  const del = async (id) => {
    if (!confirm("Delete this booking?")) return;
    await api.delete(`/bookings/${id}`);
    load();
  };

  const toggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  return (
    <div>
      <div className="overline text-primary/70">CRM</div>
      <h1 className="font-serif text-4xl mt-2 mb-8">Bookings & Enquiries</h1>
      <div className="border border-border bg-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary/60">
            <tr className="text-left">
              {["Client", "Functions / Event", "Date", "Location", "Region", "Status", "Actions"].map(h => (
                <th key={h} className="p-3 overline text-muted-foreground">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(!Array.isArray(rows) || rows.length === 0) && (
              <tr><td colSpan={7} className="p-6 text-muted-foreground text-center">No bookings.</td></tr>
            )}
            {Array.isArray(rows) && rows.map(r => {
              const isExpanded = expandedId === r.id;
              return (
                <React.Fragment key={r.id}>
                  <tr className="border-t border-border hover:bg-secondary/20 transition-colors" data-testid={`admin-booking-${r.id}`}>
                    <td className="p-3">
                      <div className="font-medium">{r.client_name}</div>
                      <div className="text-xs text-muted-foreground">{r.client_email}</div>
                      {r.client_phone && <div className="text-xs text-muted-foreground">{r.client_phone}</div>}
                    </td>
                    <td className="p-3 max-w-[200px] truncate" title={r.event_type}>{r.event_type}</td>
                    <td className="p-3">{r.event_date ? new Date(r.event_date).toLocaleDateString() : "—"}</td>
                    <td className="p-3">{r.event_location}</td>
                    <td className="p-3 capitalize">{r.region}</td>
                    <td className="p-3">
                      <StatusSelect
                        value={r.status}
                        onChange={(v) => updateStatus(r.id, v)}
                        options={["pending", "confirmed", "in_progress", "completed", "cancelled"]}
                        testid={`status-${r.id}`}
                      />
                    </td>
                    <td className="p-3 flex items-center gap-3">
                      <button
                        onClick={() => toggleExpand(r.id)}
                        className="text-xs text-primary underline flex items-center gap-1 font-mono uppercase tracking-wider"
                      >
                        {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                        {isExpanded ? "Hide Details" : "View Details"}
                      </button>
                      <button onClick={() => del(r.id)} className="text-destructive">
                        <Trash2 size={14} strokeWidth={1.25} />
                      </button>
                    </td>
                  </tr>

                  {isExpanded && (
                    <tr className="bg-secondary/40 border-t border-border">
                      <td colSpan={7} className="p-6">
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 text-xs font-mono mb-4">
                          <div>
                            <span className="text-muted-foreground uppercase tracking-widest block mb-1">Budget</span>
                            <span className="text-sm font-serif font-medium">{r.budget ? `PKR ${r.budget.toLocaleString()}` : "Not specified"}</span>
                          </div>
                          <div>
                            <span className="text-muted-foreground uppercase tracking-widest block mb-1">Consultation Type</span>
                            <span className="text-sm">{r.consultation_type || "Not specified"}</span>
                          </div>
                          <div>
                            <span className="text-muted-foreground uppercase tracking-widest block mb-1">Currency Preference</span>
                            <span className="text-sm">{r.currency_preference || "PKR"}</span>
                          </div>
                          <div>
                            <span className="text-muted-foreground uppercase tracking-widest block mb-1">Submission Date</span>
                            <span className="text-sm">{r.created_at ? new Date(r.created_at).toLocaleString() : "—"}</span>
                          </div>
                        </div>

                        <div className="mb-4">
                          <span className="text-muted-foreground uppercase tracking-widest block mb-1 text-xs font-mono">Selected Add-ons</span>
                          <div className="flex flex-wrap gap-1.5 mt-1">
                            {r.addons && r.addons.length > 0 ? (
                              r.addons.map((addon, idx) => (
                                <span key={idx} className="px-2.5 py-1 bg-background border border-border text-xs font-sans">
                                  {addon}
                                </span>
                              ))
                            ) : (
                              <span className="text-xs text-muted-foreground">No add-ons selected</span>
                            )}
                          </div>
                        </div>

                        <div>
                          <span className="text-muted-foreground uppercase tracking-widest block mb-1 text-xs font-mono">Additional Notes / Questionnaire Extra Info</span>
                          <p className="text-sm font-sans bg-background p-4 border border-border leading-relaxed whitespace-pre-wrap">
                            {r.notes || "No additional notes provided."}
                          </p>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PortfolioPage() {
  const [items, setItems] = useState([]);
  const [stories, setStories] = useState([]);
  const [tab, setTab] = useState("file");
  const [form, setForm] = useState({ category: "Wedding Film", region: "pakistan", title: "", visibility: "public", youtubeUrl: "" });
  const [storyForm, setStoryForm] = useState({ couple: "", location: "", date: "", count: "28 Photographs" });
  const [storyFiles, setStoryFiles] = useState([]);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const load = () => {
    api.get("/media", { params: { visibility: "public" } }).then(({ data }) => setItems(Array.isArray(data) ? data : [])).catch(() => setItems([]));
    api.get("/wedding-stories").then(({ data }) => setStories(Array.isArray(data) ? data : [])).catch(() => setStories([]));
  };
  useEffect(() => { load(); }, []);

  const handleUpload = async (e) => {
    e.preventDefault();
    setUploading(true);

    try {
      if (tab === "youtube") {
        if (!form.youtubeUrl) return toast.error("Please enter a YouTube link or ID");
        await api.post("/media", {
          title: form.title || "Featured Wedding Film",
          category: form.category,
          region: form.region ? form.region.toLowerCase() : "pakistan",
          visibility: "public",
          url: form.youtubeUrl,
        });
      } else if (tab === "story") {
        if (!storyForm.couple) return toast.error("Please enter couple name");
        if (storyFiles.length === 0) return toast.error("Please select at least one image from your folder");

        const galleryUrls = [];
        for (let i = 0; i < storyFiles.length; i++) {
          const fd = new FormData();
          fd.append("file", storyFiles[i]);
          fd.append("category", "wedding");
          fd.append("visibility", "public");
          fd.append("region", "pakistan");
          const { data } = await api.post("/media/upload", fd);
          if (data && data.id) {
            galleryUrls.push(`${API}/media/file/${data.id}`);
          }
        }

        await api.post("/wedding-stories", {
          couple: storyForm.couple,
          location: storyForm.location || "Lahore",
          date: storyForm.date || "2026",
          cover: galleryUrls[0] || "",
          count: `${galleryUrls.length} Photographs`,
          gallery: galleryUrls,
        });
      } else {
        if (!file) return toast.error("Choose a file to upload");
        const fd = new FormData();
        fd.append("category", form.category);
        fd.append("region", form.region ? form.region.toLowerCase() : "pakistan");
        fd.append("visibility", "public");
        fd.append("file", file);
        fd.append("title", form.title || file.name);
        await api.post("/media/upload", fd);
      }

      toast.success(tab === "youtube" ? "YouTube Film Added" : tab === "story" ? "Full Wedding Story Folder Created Successfully" : "Media Uploaded Successfully");

      setFile(null);
      setStoryFiles([]);
      setForm({ category: "Wedding Film", region: "pakistan", title: "", visibility: "public", youtubeUrl: "" });
      setStoryForm({ couple: "", location: "", date: "", count: "28 Photographs" });
      load();
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Operation failed");
    } finally {
      setUploading(false);
    }
  };

  const del = async (id) => {
    if (!confirm("Delete this media item?")) return;
    await api.delete(`/media/${id}`);
    load();
  };

  const delStory = async (id) => {
    if (!confirm("Delete this wedding story folder?")) return;
    await api.delete(`/wedding-stories/${id}`);
    load();
  };

  return (
    <div>
      <div className="overline text-primary/70">Media Library</div>
      <h1 className="font-serif text-4xl mt-2 mb-8">Portfolio Manager</h1>

      <div className="flex gap-4 mb-6 border-b border-border pb-4 overflow-x-auto">
        <button
          type="button"
          onClick={() => setTab("file")}
          className={`text-xs uppercase tracking-widest pb-2 font-semibold transition-colors whitespace-nowrap ${tab === "file" ? "text-primary border-b-2 border-primary" : "text-muted-foreground hover:text-foreground"}`}
        >
          Upload Photo / File
        </button>
        <button
          type="button"
          onClick={() => setTab("youtube")}
          className={`text-xs uppercase tracking-widest pb-2 font-semibold transition-colors whitespace-nowrap ${tab === "youtube" ? "text-primary border-b-2 border-primary" : "text-muted-foreground hover:text-foreground"}`}
        >
          Add YouTube Film Link
        </button>
        <button
          type="button"
          onClick={() => setTab("story")}
          className={`text-xs uppercase tracking-widest pb-2 font-semibold transition-colors whitespace-nowrap ${tab === "story" ? "text-primary border-b-2 border-primary" : "text-muted-foreground hover:text-foreground"}`}
        >
          Create Full Wedding Story Folder
        </button>
      </div>

      <form onSubmit={handleUpload} className="border border-border bg-card p-6 mb-8 grid grid-cols-1 md:grid-cols-4 gap-4 items-end" data-testid="upload-form">
        {tab === "file" ? (
          <div className="md:col-span-2">
            <label className="overline text-muted-foreground">Select File (Image/Video)</label>
            <input type="file" accept="image/*,video/*" onChange={(e) => setFile(e.target.files?.[0])}
              className="w-full text-sm mt-2" data-testid="upload-file" />
          </div>
        ) : tab === "youtube" ? (
          <div className="md:col-span-2">
            <label className="overline text-muted-foreground">YouTube Video URL or ID</label>
            <input 
              type="text" 
              placeholder="e.g. https://youtu.be/XYZ123 or XYZ123"
              value={form.youtubeUrl} 
              onChange={(e) => setForm({ ...form, youtubeUrl: e.target.value })}
              className="w-full border border-input bg-background px-3 py-2 mt-2 text-sm" 
              data-testid="youtube-url-input" 
            />
          </div>
        ) : (
          <>
            <div>
              <label className="overline text-muted-foreground">Couple Name</label>
              <input required type="text" placeholder="e.g. Ayesha & Arham" value={storyForm.couple} onChange={(e) => setStoryForm({ ...storyForm, couple: e.target.value })} className="w-full border border-input bg-background px-3 py-2 mt-2 text-sm" />
            </div>
            <div>
              <label className="overline text-muted-foreground">Location / Venue</label>
              <input type="text" placeholder="e.g. Bhurban, Murree" value={storyForm.location} onChange={(e) => setStoryForm({ ...storyForm, location: e.target.value })} className="w-full border border-input bg-background px-3 py-2 mt-2 text-sm" />
            </div>
            <div>
              <label className="overline text-muted-foreground">Event Date / Season</label>
              <input type="text" placeholder="e.g. Autumn 2025" value={storyForm.date} onChange={(e) => setStoryForm({ ...storyForm, date: e.target.value })} className="w-full border border-input bg-background px-3 py-2 mt-2 text-sm" />
            </div>
            <div>
              <label className="overline text-muted-foreground">Photo Count Badge</label>
              <input type="text" placeholder="e.g. 28 Photographs" value={storyForm.count} onChange={(e) => setStoryForm({ ...storyForm, count: e.target.value })} className="w-full border border-input bg-background px-3 py-2 mt-2 text-sm" />
            </div>
            <div className="md:col-span-4">
              <label className="overline text-muted-foreground block mb-2">Select Multiple Images From Your Device Folder</label>
              <input 
                type="file" 
                multiple 
                accept="image/*" 
                onChange={(e) => setStoryFiles(Array.from(e.target.files || []))} 
                className="w-full text-sm border border-input bg-background p-2" 
              />
              <p className="text-xs text-muted-foreground mt-1.5 font-mono">
                {storyFiles.length > 0 ? `${storyFiles.length} images selected from folder.` : "No images selected yet."} First image will be used as cover automatically.
              </p>
            </div>
          </>
        )}

        {tab !== "story" && (
          <>
            <div>
              <label className="overline text-muted-foreground">Title</label>
              <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="e.g. A Love Story in Lahore"
                className="w-full border border-input bg-background px-3 py-2 mt-2 text-sm" />
            </div>

            <div>
              <label className="overline text-muted-foreground">Region</label>
              <select value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })}
                className="w-full border border-input bg-background px-3 py-2 mt-2 text-sm" data-testid="upload-region">
                <option value="pakistan">Pakistan</option>
                <option value="international">International</option>
              </select>
            </div>
          </>
        )}

        <div className="md:col-span-4 flex justify-end mt-2">
          <button type="submit" disabled={uploading} className="btn-primary-filled h-10 px-6 disabled:opacity-60" data-testid="upload-submit">
            <Upload size={14} strokeWidth={1.25} className="mr-2" />{uploading ? "Uploading & Saving…" : tab === "youtube" ? "Add YouTube Film" : tab === "story" ? "Create Wedding Story Folder" : "Upload File"}
          </button>
        </div>
      </form>

      {/* Existing Full Wedding Stories List */}
      <div className="mb-12">
        <h3 className="font-serif text-2xl mb-4">Manage Full Wedding Stories</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {(!Array.isArray(stories) || stories.length === 0) && <div className="text-sm text-muted-foreground col-span-full">No wedding stories created yet.</div>}
          {Array.isArray(stories) && stories.map(s => (
            <div key={s.id} className="border border-border bg-card p-4 flex gap-4 items-center justify-between">
              <div className="flex gap-3 items-center">
                <img src={s.cover ? `${s.cover}?w=100` : s.cover} alt={s.couple} loading="lazy" decoding="async" className="w-14 h-14 object-cover rounded-[2px]" />
                <div>
                  <div className="font-serif font-medium">{s.couple}</div>
                  <div className="text-xs text-muted-foreground">{s.location} · {s.date}</div>
                </div>
              </div>
              <button onClick={() => delStory(s.id)} className="text-destructive p-2"><Trash2 size={14} strokeWidth={1.25} /></button>
            </div>
          ))}
        </div>
      </div>

      <h3 className="font-serif text-2xl mb-4">Media Library Archive</h3>
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
        {(!Array.isArray(items) || items.length === 0) && <div className="text-sm text-muted-foreground col-span-full">No media items added yet.</div>}
        {Array.isArray(items) && items.map(m => (
          <div key={m.id} className="border border-border bg-card relative group" data-testid={`media-${m.id}`}>
            <div className="aspect-square bg-secondary overflow-hidden">
              {m.media_type === "youtube" || (m.url && m.url.includes("youtube")) ? (
                <div className="w-full h-full bg-[#141F14] flex flex-col items-center justify-center text-xs text-[#C9D1B5] p-2 text-center">
                  <Play size={20} className="mb-1" />
                  YouTube Film
                </div>
              ) : m.media_type === "video" ? (
                <video src={`${API}/media/file/${m.id}`} preload="metadata" className="w-full h-full object-cover" muted />
              ) : (
                <img src={`${API}/media/file/${m.id}?w=400`} alt={m.title} loading="lazy" decoding="async" className="w-full h-full object-cover" />
              )}
            </div>
            <div className="p-2 flex justify-between items-center text-xs">
              <span className="truncate mr-2 font-medium">{m.title}</span>
              <button onClick={() => del(m.id)} className="text-destructive"><Trash2 size={12} strokeWidth={1.25} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ProjectsPage() {
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({});
  const [editingId, setEditingId] = useState(null);

  const load = () => {
    api.get("/projects").then(({ data }) => setRows(Array.isArray(data) ? data : [])).catch(() => setRows([]));
    api.get("/users").then(({ data }) => setUsers(Array.isArray(data) ? data.filter(u => u.role === "client") : [])).catch(() => setUsers([]));
  };
  useEffect(() => { load(); }, []);

  const startCreate = () => {
    setForm({ title: "", client_id: "", status: "planning", progress: 0, description: "", event_date: "" });
    setEditingId(null);
    setCreating(true);
  };
  const startEdit = (r) => {
    setForm({
      title: r.title || "",
      client_id: r.client_id || "",
      status: r.status || "planning",
      progress: r.progress || 0,
      description: r.description || "",
      event_date: r.event_date ? r.event_date.split("T")[0] : ""
    });
    setEditingId(r.id);
    setCreating(true);
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await api.put(`/projects/${editingId}`, form);
        toast.success("Updated");
      } else {
        await api.post(`/projects`, form);
        toast.success("Created");
      }
      setCreating(false);
      load();
    } catch (e) {
      const msg = e?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Failed");
    }
  };

  const del = async (id) => {
    if (!confirm("Delete this record?")) return;
    await api.delete(`/projects/${id}`);
    load();
  };

  return (
    <div>
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="overline text-primary/70">CRM</div>
          <h1 className="font-serif text-4xl mt-2">Projects</h1>
        </div>
        <button onClick={startCreate} className="btn-primary-filled" data-testid="add-projects">
          <Plus size={14} strokeWidth={1.25} className="mr-2" /> Add
        </button>
      </div>

      {creating && (
        <form onSubmit={save} className="border border-border bg-card p-6 mb-8 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="form-projects">
          <div>
            <label className="overline text-muted-foreground">Title</label>
            <input required value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full border border-input bg-background px-3 py-2 mt-2 text-sm" />
          </div>
          <div>
            <label className="overline text-muted-foreground">Client ID (paste from client list)</label>
            <input required value={form.client_id || ""} onChange={(e) => setForm({ ...form, client_id: e.target.value })} className="w-full border border-input bg-background px-3 py-2 mt-2 text-sm" />
          </div>
          <div>
            <label className="overline text-muted-foreground">Status</label>
            <select value={form.status || "planning"} onChange={(e) => setForm({ ...form, status: e.target.value })} className="w-full border border-input bg-background px-3 py-2 mt-2 text-sm">
              <option value="planning">Planning</option>
              <option value="shooting">Shooting</option>
              <option value="editing">Editing</option>
              <option value="review">Review</option>
              <option value="delivered">Delivered</option>
            </select>
          </div>
          <div>
            <label className="overline text-muted-foreground">Progress %</label>
            <input type="number" value={form.progress || 0} onChange={(e) => setForm({ ...form, progress: Number(e.target.value) })} className="w-full border border-input bg-background px-3 py-2 mt-2 text-sm" />
          </div>
          <div>
            <label className="overline text-muted-foreground">Event Date</label>
            <input type="date" value={form.event_date || ""} onChange={(e) => setForm({ ...form, event_date: e.target.value })} className="w-full border border-input bg-background px-3 py-2 mt-2 text-sm" />
          </div>
          <div className="md:col-span-2">
            <label className="overline text-muted-foreground">Description</label>
            <textarea rows={3} value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full border border-input bg-background px-3 py-2 mt-2 text-sm" />
          </div>
          <div className="md:col-span-2 flex gap-3">
            <button type="submit" className="btn-primary-filled">{editingId ? "Update" : "Create"}</button>
            <button type="button" onClick={() => setCreating(false)} className="btn-primary">Cancel</button>
          </div>
        </form>
      )}

      <div className="border border-border bg-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary/60">
            <tr className="text-left">
              <th className="p-3 overline text-muted-foreground">Title</th>
              <th className="p-3 overline text-muted-foreground">Client</th>
              <th className="p-3 overline text-muted-foreground">Status</th>
              <th className="p-3 overline text-muted-foreground">Progress</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {(!Array.isArray(rows) || rows.length === 0) && <tr><td colSpan={5} className="p-6 text-muted-foreground text-center">No records.</td></tr>}
            {Array.isArray(rows) && rows.map(r => (
              <tr key={r.id} className="border-t border-border">
                <td className="p-3 font-medium">{r.title}</td>
                <td className="p-3">{users.find(u => u.id === r.client_id)?.name || r.client_id?.slice(0, 8) || "—"}</td>
                <td className="p-3 capitalize">{r.status}</td>
                <td className="p-3">{r.progress || 0}%</td>
                <td className="p-3 text-right">
                  <button onClick={() => startEdit(r)} className="text-primary mr-3"><Edit2 size={14} strokeWidth={1.25} /></button>
                  <button onClick={() => del(r.id)} className="text-destructive"><Trash2 size={14} strokeWidth={1.25} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function EventsPage() {
  const [rows, setRows] = useState([]);
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    title: "", client_name: "", event_type: "", event_date: "",
    location: "", status: "scheduled", notes: ""
  });

  const load = () => {
    api.get("/events").then(({ data }) => setRows(Array.isArray(data) ? data : [])).catch(() => setRows([]));
  };

  useEffect(() => { load(); }, []);

  const startCreate = () => {
    setForm({ title: "", client_name: "", event_type: "", event_date: "", location: "", status: "scheduled", notes: "" });
    setEditingId(null);
    setCreating(true);
  };

  const startEdit = (r) => {
    setForm({
      title: r.title || "",
      client_name: r.client_name || "",
      event_type: r.event_type || "",
      event_date: r.event_date ? r.event_date.split("T")[0] : "",
      location: r.location || "",
      status: r.status || "scheduled",
      notes: r.notes || ""
    });
    setEditingId(r.id);
    setCreating(true);
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await api.put(`/events/${editingId}`, form);
        toast.success("Event updated");
      } else {
        await api.post(`/events`, form);
        toast.success("Event created");
      }
      setCreating(false);
      load();
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Failed to save event");
    }
  };

  const del = async (id) => {
    if (!confirm("Delete this event?")) return;
    await api.delete(`/events/${id}`);
    load();
  };

  return (
    <div>
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="overline text-primary/70">Operations</div>
          <h1 className="font-serif text-4xl mt-2">Events Manager</h1>
        </div>
        <button onClick={startCreate} className="btn-primary-filled" data-testid="add-events">
          <Plus size={14} strokeWidth={1.25} className="mr-2" /> Add Event
        </button>
      </div>

      {creating && (
        <form onSubmit={save} className="border border-border bg-card p-6 mb-8 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="form-events">
          <div>
            <label className="overline text-muted-foreground">Title</label>
            <input required value={form.title} onChange={e => setForm({...form, title: e.target.value})} className="w-full border border-input bg-background px-3 py-2 mt-2 text-sm" />
          </div>
          <div>
            <label className="overline text-muted-foreground">Client Name</label>
            <input required value={form.client_name} onChange={e => setForm({...form, client_name: e.target.value})} className="w-full border border-input bg-background px-3 py-2 mt-2 text-sm" />
          </div>
          <div>
            <label className="overline text-muted-foreground">Event Type</label>
            <input value={form.event_type} onChange={e => setForm({...form, event_type: e.target.value})} className="w-full border border-input bg-background px-3 py-2 mt-2 text-sm" />
          </div>
          <div>
            <label className="overline text-muted-foreground">Event Date</label>
            <input type="date" value={form.event_date} onChange={e => setForm({...form, event_date: e.target.value})} className="w-full border border-input bg-background px-3 py-2 mt-2 text-sm" />
          </div>
          <div>
            <label className="overline text-muted-foreground">Location</label>
            <input value={form.location} onChange={e => setForm({...form, location: e.target.value})} className="w-full border border-input bg-background px-3 py-2 mt-2 text-sm" />
          </div>
          <div>
            <label className="overline text-muted-foreground">Status</label>
            <select value={form.status} onChange={e => setForm({...form, status: e.target.value})} className="w-full border border-input bg-background px-3 py-2 mt-2 text-sm">
              <option value="scheduled">Scheduled</option>
              <option value="ongoing">Ongoing</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="overline text-muted-foreground">Notes</label>
            <textarea rows={3} value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} className="w-full border border-input bg-background px-3 py-2 mt-2 text-sm" />
          </div>
          <div className="md:col-span-2 flex gap-3">
            <button type="submit" className="btn-primary-filled">{editingId ? "Update Event" : "Create Event"}</button>
            <button type="button" onClick={() => setCreating(false)} className="btn-primary">Cancel</button>
          </div>
        </form>
      )}

      <div className="border border-border bg-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary/60">
            <tr className="text-left">
              {["Event Title", "Client", "Date", "Location", "Status", "Actions"].map(h => (
                <th key={h} className="p-3 overline text-muted-foreground">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(!Array.isArray(rows) || rows.length === 0) && <tr><td colSpan={6} className="p-6 text-muted-foreground text-center">No events found. Confirmed bookings will automatically appear here.</td></tr>}
            {Array.isArray(rows) && rows.map(r => (
              <tr key={r.id} className="border-t border-border">
                <td className="p-3 font-medium">{r.title}</td>
                <td className="p-3">{r.client_name}</td>
                <td className="p-3">{r.event_date ? new Date(r.event_date).toLocaleDateString() : "—"}</td>
                <td className="p-3">{r.location || "—"}</td>
                <td className="p-3 capitalize">{r.status}</td>
                <td className="p-3 text-right flex items-center justify-end gap-3">
                  <button onClick={() => startEdit(r)} className="text-primary"><Edit2 size={14} strokeWidth={1.25} /></button>
                  <button onClick={() => del(r.id)} className="text-destructive"><Trash2 size={14} strokeWidth={1.25} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TasksPage() {
  const [rows, setRows] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({});
  const [editingId, setEditingId] = useState(null);

  const load = () => api.get("/tasks").then(({ data }) => setRows(Array.isArray(data) ? data : [])).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const startCreate = () => {
    setForm({ title: "", priority: "medium", status: "todo", due_date: "", description: "" });
    setEditingId(null);
    setCreating(true);
  };
  const startEdit = (r) => {
    setForm({
      title: r.title || "",
      priority: r.priority || "medium",
      status: r.status || "todo",
      due_date: r.due_date ? r.due_date.split("T")[0] : "",
      description: r.description || ""
    });
    setEditingId(r.id);
    setCreating(true);
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await api.put(`/tasks/${editingId}`, form);
        toast.success("Updated");
      } else {
        await api.post(`/tasks`, form);
        toast.success("Created");
      }
      setCreating(false);
      load();
    } catch (e) {
      const msg = e?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Failed");
    }
  };

  const del = async (id) => {
    if (!confirm("Delete this record?")) return;
    await api.delete(`/tasks/${id}`);
    load();
  };

  return (
    <div>
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="overline text-primary/70">CRM</div>
          <h1 className="font-serif text-4xl mt-2">Tasks</h1>
        </div>
        <button onClick={startCreate} className="btn-primary-filled" data-testid="add-tasks">
          <Plus size={14} strokeWidth={1.25} className="mr-2" /> Add
        </button>
      </div>

      {creating && (
        <form onSubmit={save} className="border border-border bg-card p-6 mb-8 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="form-tasks">
          <div>
            <label className="overline text-muted-foreground">Title</label>
            <input required value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full border border-input bg-background px-3 py-2 mt-2 text-sm" />
          </div>
          <div>
            <label className="overline text-muted-foreground">Priority</label>
            <select value={form.priority || "medium"} onChange={(e) => setForm({ ...form, priority: e.target.value })} className="w-full border border-input bg-background px-3 py-2 mt-2 text-sm">
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
          </div>
          <div>
            <label className="overline text-muted-foreground">Status</label>
            <select value={form.status || "todo"} onChange={(e) => setForm({ ...form, status: e.target.value })} className="w-full border border-input bg-background px-3 py-2 mt-2 text-sm">
              <option value="todo">Todo</option>
              <option value="in_progress">In Progress</option>
              <option value="done">Done</option>
            </select>
          </div>
          <div>
            <label className="overline text-muted-foreground">Due Date</label>
            <input type="date" value={form.due_date || ""} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="w-full border border-input bg-background px-3 py-2 mt-2 text-sm" />
          </div>
          <div className="md:col-span-2">
            <label className="overline text-muted-foreground">Description</label>
            <textarea rows={3} value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full border border-input bg-background px-3 py-2 mt-2 text-sm" />
          </div>
          <div className="md:col-span-2 flex gap-3">
            <button type="submit" className="btn-primary-filled">{editingId ? "Update" : "Create"}</button>
            <button type="button" onClick={() => setCreating(false)} className="btn-primary">Cancel</button>
          </div>
        </form>
      )}

      <div className="border border-border bg-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary/60">
            <tr className="text-left">
              <th className="p-3 overline text-muted-foreground">Title</th>
              <th className="p-3 overline text-muted-foreground">Priority</th>
              <th className="p-3 overline text-muted-foreground">Status</th>
              <th className="p-3 overline text-muted-foreground">Due</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {(!Array.isArray(rows) || rows.length === 0) && <tr><td colSpan={5} className="p-6 text-muted-foreground text-center">No records.</td></tr>}
            {Array.isArray(rows) && rows.map(r => (
              <tr key={r.id} className="border-t border-border">
                <td className="p-3 font-medium">{r.title}</td>
                <td className="p-3 capitalize">{r.priority}</td>
                <td className="p-3 capitalize">{r.status?.replace("_", " ")}</td>
                <td className="p-3">{r.due_date ? new Date(r.due_date).toLocaleDateString() : "—"}</td>
                <td className="p-3 text-right">
                  <button onClick={() => startEdit(r)} className="text-primary mr-3"><Edit2 size={14} strokeWidth={1.25} /></button>
                  <button onClick={() => del(r.id)} className="text-destructive"><Trash2 size={14} strokeWidth={1.25} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  return (
    <DashboardLayout role="admin">
      <Routes>
        <Route index element={<Overview />} />
        <Route path="bookings" element={<BookingsPage />} />
        <Route path="portfolio" element={<PortfolioPage />} />
        <Route path="projects" element={<ProjectsPage />} />
        <Route path="events" element={<EventsPage />} />
        <Route path="tasks" element={<TasksPage />} />
      </Routes>
    </DashboardLayout>
  );
}